import { useState, useEffect, useRef, useMemo } from 'react'
import { useMetaData } from '../queries/useMetaData.js'
import { useGoalsData } from '../queries/useGoalsData.js'
import {
  useSaveTeamUpdate,
  useGenerateTeamUpdate,
} from '../mutations/useSaveTeamUpdate.js'
import { useUIStore } from '../store/uiStore.js'
import { useIsReadOnly } from '../store/archiveViewStore.js'
import { getTeamUpdateAuthoritative } from '../data/teamUpdateOverrides.js'
import { colorFor } from '../data/userColors.js'
import { timeAgo } from '../data/metaKeys.js'

const TEAM_MEMBERS = [
  'Ricky', 'Zoe', 'Christina', 'Katie', 'Margo', 'Emily',
  'Sandra', 'Sergina', 'Jonathan', 'Chris', 'Harold',
  'Jessie', 'Christine', 'Braden', 'Colin', 'Jane',
]

/**
 * TeamUpdateCard — per-team TLDR / Wins / Flags card, shown on the
 * Team Updates pillar.
 *
 * Mirrors the UX of WeeklySummary (click-to-edit, save on blur,
 * autosync from Ably) but is keyed by teamId (gtm/delivery/product)
 * rather than goalKey.
 *
 * DRI is editable in-place via a small picker; its absence in a save
 * patch preserves whatever the server has (so teammates can update
 * TLDR/Wins/Flags without clobbering the DRI).
 */
export default function TeamUpdateCard({ team }) {
  const currentUser = useUIStore((s) => s.currentUser)
  const { data: meta } = useMetaData()
  const { data: goalsData } = useGoalsData()
  const saveMutation = useSaveTeamUpdate()
  const generateMutation = useGenerateTeamUpdate()
  const readOnly = useIsReadOnly()
  const [generateError, setGenerateError] = useState(null)

  const serverEntry = meta?.teamUpdates?.[team.id] || null
  const override = getTeamUpdateAuthoritative(team.id, serverEntry)
  const saved = override ?? serverEntry ?? {}

  const dri = saved.dri || team.defaultDri
  const driColor = colorFor(dri)

  // Compute the goals + sub-goals "owned" by this team, defined as
  // anything where the current DRI matches the team's update DRI.
  // The match shifts when the DRI is reassigned via the picker.
  // Also pull each matched goal's weekly summary so the AI generation
  // has the actual TLDR/Wins/Flags content (not just the goal title).
  const ownedInputs = useMemo(() => {
    if (!Array.isArray(goalsData) || !dri) {
      return { goals: [], subgoals: [] }
    }
    const summaryMap = meta?.weeklySummary || {}
    const goals = []
    const subgoals = []
    for (let pi = 0; pi < goalsData.length; pi++) {
      const pillar = goalsData[pi]
      if (!pillar?.goals) continue
      for (let gi = 0; gi < pillar.goals.length; gi++) {
        const g = pillar.goals[gi]
        if (!g || g.type === 'section') continue
        // Goal-level match: include if the goal's DRI is this team's DRI
        // AND the goal has a weekly summary written this week.
        if (g.dri === dri) {
          const summary = summaryMap[`p${pi}-g${gi}`] || {}
          const hasContent = !!(summary.tldr || summary.wins || summary.flags)
          if (hasContent) {
            goals.push({
              goalName: g.goal || 'Untitled goal',
              pillar: pillar.pillar,
              dri: g.dri,
              tldr: summary.tldr || '',
              wins: summary.wins || '',
              flags: summary.flags || '',
              savedBy: summary.savedBy || '',
            })
          }
        }
        // Sub-goal-level match: pull any sub-goal whose owner equals
        // the team DRI and has either a current/target update or
        // free-text update content.
        const subs = g.subgoals || g.subGoals
        if (Array.isArray(subs)) {
          for (const s of subs) {
            if (!s || s.owner !== dri) continue
            const hasContent = !!(
              s.update ||
              (s.current !== undefined && s.current !== null && s.current !== '')
            )
            if (!hasContent) continue
            subgoals.push({
              pillar: pillar.pillar,
              goalName: g.goal || 'Untitled goal',
              subgoalName: s.subgoal || 'Untitled sub-goal',
              owner: s.owner,
              current: s.current,
              target: s.target,
              unit: s.unit || '',
              update: s.update || '',
            })
          }
        }
      }
    }
    return { goals, subgoals }
  }, [goalsData, meta?.weeklySummary, dri])

  const matchCount = ownedInputs.goals.length + ownedInputs.subgoals.length

  const handleGenerate = async () => {
    if (readOnly) return
    if (matchCount === 0) return
    setGenerateError(null)
    // If the lead already has manual content, confirm before overwriting.
    const hasManualContent = !!(saved.tldr || saved.wins || saved.flags)
    if (hasManualContent) {
      const ok = confirm(
        'This will replace the current TLDR / Wins / Flags with a fresh AI draft from the team\'s goal updates. Continue?\n\n(You can press Cmd+Z afterward to revert.)'
      )
      if (!ok) return
    }
    try {
      const result = await generateMutation.mutateAsync({
        teamName: team.name,
        teamDri: dri,
        goals: ownedInputs.goals,
        subgoals: ownedInputs.subgoals,
      })
      // Write all three fields in a single patch — one POST, one
      // optimistic cache update, one Ably broadcast. Same shape
      // saveField uses for individual edits.
      const patch = {
        tldr: result.tldr || '',
        wins: result.wins || '',
        flags: result.flags || '',
        savedAt: Date.now(),
        savedBy: currentUser || 'Unknown',
      }
      saveMutation.mutate({ teamId: team.id, patch })
      // Cancel any in-flight inline edit so the new draft shows
      // immediately rather than being overlaid by a stale draft.
      setEditing(null)
    } catch (err) {
      console.error('[team-update] generate failed', err)
      setGenerateError(
        err?.message || "Couldn't generate — check the worker logs."
      )
    }
  }

  const [draft, setDraft] = useState({
    tldr: saved.tldr || '',
    wins: saved.wins || '',
    flags: saved.flags || '',
    fyis: saved.fyis || '',
  })
  const [editing, setEditing] = useState(null) // 'tldr' | 'wins' | 'flags' | 'fyis' | null

  // Keep draft in sync with server updates when not actively editing
  useEffect(() => {
    if (editing) return
    setDraft({
      tldr: saved.tldr || '',
      wins: saved.wins || '',
      flags: saved.flags || '',
      fyis: saved.fyis || '',
    })
  }, [saved.tldr, saved.wins, saved.flags, saved.fyis, editing])

  // persistField / saveField / autosave — see WeeklySummary for the
  // rationale. Short version: patches are concurrency-safe, and the
  // debounced autosave covers interrupted sessions where the user
  // never gets a chance to blur.
  const persistField = (field, newValue) => {
    if (newValue === (saved[field] || '')) return
    const patch = {
      [field]: newValue,
      savedAt: Date.now(),
      savedBy: currentUser || 'Unknown',
    }
    saveMutation.mutate({ teamId: team.id, patch })
  }

  const saveField = (field) => {
    persistField(field, draft[field])
    setEditing(null)
  }

  const activeDraftValue = editing ? draft[editing] : null
  const latestDraftRef = useRef(draft)
  const latestEditingRef = useRef(editing)
  latestDraftRef.current = draft
  latestEditingRef.current = editing
  useEffect(() => {
    if (!editing) return
    const timer = setTimeout(() => {
      persistField(editing, draft[editing])
    }, 2000)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDraftValue, editing])
  // Flush-on-unmount: save the active draft if the card is about to go
  // away (user switches pillars, closes the panel). Without this, a
  // typed-but-unblurred TLDR/Wins/Flags is lost on navigation.
  useEffect(() => {
    return () => {
      const field = latestEditingRef.current
      if (!field) return
      const value = latestDraftRef.current?.[field]
      if (value === undefined) return
      persistField(field, value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const saveDri = (nextDri) => {
    const patch = {
      dri: nextDri,
      savedAt: Date.now(),
      savedBy: currentUser || 'Unknown',
    }
    saveMutation.mutate({ teamId: team.id, patch })
  }

  const fields = [
    {
      field: 'tldr',
      label: 'TLDR',
      dot: '🟣',
      color: '#9333ea',
      bg: '#faf5ff',
      border: '#e9d5ff',
      hint: `Click to summarize what the ${team.name} team shipped / is focused on this week`,
      placeholder:
        `Your ${team.name} team's view of the week — 2-3 sentences on what shipped, what's in flight, and what's up next. AI can draft this from your team's goal updates; edit to refine.`,
      minH: 72,
    },
    {
      field: 'wins',
      label: 'Wins',
      dot: '🟢',
      color: '#16a34a',
      bg: '#f0fdf4',
      border: '#bbf7d0',
      hint: `Click to call out ${team.name}'s wins this week`,
      placeholder:
        `Wins your ${team.name} team owned this week. Specifics — deals closed, milestones hit, features shipped. AI drafts from team goals; edit to refine.`,
      minH: 64,
    },
    {
      field: 'flags',
      label: 'Flags / Blockers / Risks',
      dot: '🟡',
      color: '#d97706',
      bg: '#fffbeb',
      border: '#fde68a',
      hint: 'Click to flag blockers, risks, or asks for leadership',
      placeholder:
        "What's at risk or blocked on your team's work. AI drafts from team goal flags; edit to elevate the most important callouts.",
      minH: 64,
    },
    {
      field: 'fyis',
      // FYIs is the explicit channel for content that should land in the
      // Company Update on top of the team's TLDR/Wins/Flags. The label
      // and placeholder spell out the destination so leads know what
      // belongs here vs. in the team-view fields above.
      label: 'FYIs — additional context to include in the Company Update',
      dot: '🔵',
      color: '#2563eb',
      bg: '#eff6ff',
      border: '#bfdbfe',
      hint: "Click to add anything beyond your team's goal updates — hiring, cross-team callouts, conferences, training links, OOO. This is what flows into the Company Update on top of TLDR/Wins/Flags.",
      placeholder:
        'e.g., hiring updates · OOO and travel · conferences attending · training/workshop links · grants or programs · framework reflections · anything you want Zoe to weave into the company-wide update',
      minH: 56,
    },
  ]

  return (
    <div
      style={{
        background: '#fff',
        border: `1px solid var(--border)`,
        borderLeft: `4px solid ${team.color}`,
        borderRadius: 12,
        padding: 16,
        marginBottom: 14,
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
      }}
    >
      {/* Card header: team icon + name + DRI pill + saved meta */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 4,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 22 }}>{team.icon}</span>
        <h3
          style={{
            fontSize: 16,
            fontWeight: 800,
            color: team.color,
            margin: 0,
            lineHeight: 1.2,
          }}
        >
          {team.name}
        </h3>
        <DriPicker
          dri={dri}
          driColor={driColor}
          onSelect={saveDri}
          options={TEAM_MEMBERS}
          readOnly={readOnly}
        />
        <div style={{ flex: 1 }} />
        {saved.savedAt && (
          <div style={{ fontSize: 10, color: 'var(--muted-light)' }}>
            {saved.savedBy ? `${saved.savedBy} · ` : ''}{timeAgo(saved.savedAt)}
          </div>
        )}
      </div>
      {team.description && (
        <p
          style={{
            fontSize: 11,
            color: 'var(--muted)',
            margin: '0 0 8px',
            fontStyle: 'italic',
          }}
        >
          {team.description}
        </p>
      )}

      {/* Generate-from-goals control. Pulls weekly summaries from goals
          where goal.dri === team DRI and sub-goals where subgoal.owner
          === team DRI, then drafts TLDR/Wins/Flags. The lead edits the
          fields afterward to add team-level content not tied to a
          specific goal (hiring, internal wins, etc.). Hidden when in
          archive view. */}
      {!readOnly && (
        <TeamGenerateBar
          dri={dri}
          driColor={driColor}
          matchCount={matchCount}
          goalCount={ownedInputs.goals.length}
          subgoalCount={ownedInputs.subgoals.length}
          generating={generateMutation.isPending}
          hasContent={!!(saved.tldr || saved.wins || saved.flags)}
          error={generateError}
          onGenerate={handleGenerate}
        />
      )}

      {fields.map((f) => {
        const isEditing = editing === f.field
        const value = draft[f.field]
        const empty = !value

        return (
          <div
            key={f.field}
            style={{
              marginBottom: 8,
              padding: '8px 10px',
              background: f.bg,
              border: `1px solid ${f.border}`,
              borderRadius: 8,
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 800,
                color: f.color,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                marginBottom: 4,
              }}
            >
              {f.dot} {f.label}
            </div>

            {isEditing ? (
              <textarea
                autoFocus
                value={value}
                onChange={(e) => setDraft((d) => ({ ...d, [f.field]: e.target.value }))}
                onBlur={() => saveField(f.field)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setDraft((d) => ({ ...d, [f.field]: saved[f.field] || '' }))
                    setEditing(null)
                  }
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    saveField(f.field)
                  }
                }}
                placeholder={f.placeholder}
                style={{
                  width: '100%',
                  minHeight: f.minH,
                  fontSize: 12,
                  fontFamily: 'inherit',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--accent)',
                  borderRadius: 6,
                  padding: '6px 8px',
                  background: '#fff',
                  resize: 'vertical',
                  lineHeight: 1.5,
                  outline: 'none',
                }}
              />
            ) : (
              <div
                onClick={readOnly ? undefined : () => setEditing(f.field)}
                style={{
                  fontSize: 12,
                  color: empty ? 'var(--muted-light)' : 'var(--text-secondary)',
                  fontStyle: empty ? 'italic' : 'normal',
                  lineHeight: 1.5,
                  cursor: readOnly ? 'default' : 'pointer',
                  minHeight: empty ? 20 : undefined,
                  padding: '2px 0',
                  whiteSpace: 'pre-wrap',
                }}
                title={readOnly ? undefined : (empty ? f.placeholder : 'Click to edit')}
              >
                {empty
                  ? (readOnly ? '—' : f.hint)
                  : value}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/**
 * TeamGenerateBar — compact "Generate from goal updates" affordance
 * for the Team Update card.
 *
 * Disabled state: matchCount === 0. Shown but disabled with a hint
 * explaining why nothing matched (no goals owned by the current DRI,
 * or those goals don't have weekly summaries written this week).
 *
 * Enabled state: button reads "Generate from N goal updates" (or
 * "Regenerate" if the card already has saved content). Click triggers
 * the AI generation; the parent confirms before overwriting manual
 * content.
 */
function TeamGenerateBar({
  dri,
  driColor,
  matchCount,
  goalCount,
  subgoalCount,
  generating,
  hasContent,
  error,
  onGenerate,
}) {
  const enabled = matchCount > 0 && !generating
  const buttonLabel = generating
    ? 'Drafting…'
    : hasContent
      ? 'Regenerate from goal updates'
      : 'Generate from goal updates'

  const inputsLabel = (() => {
    if (matchCount === 0) {
      return `No goal updates owned by ${dri || 'the current DRI'} this week`
    }
    const parts = []
    if (goalCount) parts.push(`${goalCount} goal${goalCount === 1 ? '' : 's'}`)
    if (subgoalCount) parts.push(`${subgoalCount} sub-goal${subgoalCount === 1 ? '' : 's'}`)
    return `Drafts from ${parts.join(' + ')} owned by ${dri || 'this team'}`
  })()

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        marginBottom: 10,
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ fontSize: 14, lineHeight: 1 }}>✨</span>
      <div style={{ flex: 1, minWidth: 160 }}>
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--text-secondary)',
            margin: 0,
            letterSpacing: 0.02,
          }}
        >
          AI draft from team's goal updates
        </p>
        <p
          style={{
            fontSize: 10,
            color: matchCount === 0 ? 'var(--muted-light)' : 'var(--muted)',
            margin: '1px 0 0',
            fontStyle: matchCount === 0 ? 'italic' : 'normal',
          }}
        >
          {inputsLabel}
        </p>
        {/* Scope note — clarifies what the AI generation produces vs.
            what belongs in the FYIs field below. Cuts down on "should I
            put hiring news in TLDR or FYIs?" confusion at compose time. */}
        <p
          style={{
            fontSize: 10,
            color: 'var(--muted)',
            margin: '4px 0 0',
            lineHeight: 1.45,
          }}
        >
          Generates your team's view (TLDR / Wins / Flags). For anything else
          you want in the Company Update — hiring, cross-team callouts,
          conferences — use FYIs below.
        </p>
      </div>
      <button
        type="button"
        onClick={onGenerate}
        disabled={!enabled}
        title={
          matchCount === 0
            ? `No matching goal weekly summaries written by ${dri || 'this DRI'} yet`
            : `Pull weekly summaries from ${matchCount} matching item${matchCount === 1 ? '' : 's'} into this team's TLDR / Wins / Flags`
        }
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: enabled ? '#fff' : 'var(--muted-light)',
          background: enabled ? driColor : '#e5e7eb',
          border: 'none',
          padding: '6px 12px',
          borderRadius: 6,
          cursor: enabled ? 'pointer' : 'not-allowed',
          whiteSpace: 'nowrap',
        }}
      >
        {buttonLabel}
      </button>
      {error && (
        <p
          style={{
            flexBasis: '100%',
            fontSize: 10,
            color: '#991b1b',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 4,
            padding: '4px 8px',
            margin: '4px 0 0',
          }}
        >
          {error}
        </p>
      )}
    </div>
  )
}

/**
 * DriPicker — compact inline dropdown for reassigning the team DRI.
 * Shows the current DRI as a colored chip; click to open the list.
 */
function DriPicker({ dri, driColor, onSelect, options, readOnly }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e) => {
      if (!ref.current) return
      if (!ref.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const t = setTimeout(() => {
      document.addEventListener('click', onClick)
      document.addEventListener('keydown', onKey)
    }, 0)
    return () => {
      clearTimeout(t)
      document.removeEventListener('click', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          if (readOnly) return
          setOpen((x) => !x)
        }}
        title={readOnly ? `Team DRI` : `Team DRI — click to reassign`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 10,
          fontWeight: 700,
          color: driColor,
          background: driColor + '18',
          border: `1px solid ${driColor}30`,
          padding: '2px 8px',
          borderRadius: 99,
          cursor: readOnly ? 'default' : 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ fontSize: 9, fontWeight: 800, opacity: 0.8 }}>DRI:</span>
        {dri || 'TBD'}
        {!readOnly && <span style={{ fontSize: 8, opacity: 0.7 }}>▼</span>}
      </button>
      {!readOnly && open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            minWidth: 160,
            maxHeight: 280,
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid var(--border)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
            padding: 4,
            zIndex: 950,
          }}
        >
          {options.map((name) => {
            const c = colorFor(name)
            const selected = name === dri
            return (
              <button
                key={name}
                type="button"
                onClick={() => {
                  onSelect(name)
                  setOpen(false)
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  border: 'none',
                  background: selected ? c + '12' : 'transparent',
                  padding: '6px 8px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: selected ? 700 : 500,
                  color: 'var(--text)',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = selected ? c + '1a' : '#f9fafb')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = selected ? c + '12' : 'transparent')
                }
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: c,
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 800,
                    lineHeight: '18px',
                    textAlign: 'center',
                  }}
                >
                  {name[0]}
                </span>
                <span>{name}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
